import React, { CSSProperties } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import {
  ChartDialogConfig,
} from "../../../../../common/constants";
import TrimmingChartDialog from "../file/TrimmingChartDialog";
import { IMessageBoxParams } from "../../../../../common/MessageBox";
import { IConfirmDialogParams } from "../../../../../common/ConfirmDialog";

import AuthService from "../../../../../common/AuthService";
import BackButton from "../../../common/BackButton";
import { IProjectRow } from "../../../../../webapi/service/getProjects";
import { IFileRow } from "../../../../../webapi/service/getFIles";
import { IProtData } from "../../../../../webapi/service/getFIle";
import "../../../../main.css";
import "../../../../../App.css";
import ResultsSimulation from "./ResultsSimulation";
import { getSimulationResults, ISimulationResultsRow } from "../../../../../webapi/service/getSimulationResults";

export interface IProps extends WithTranslation {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * メインスペースの幅
   */
  mainSpaceWidth: number;
  /**
   * 選択されたプロジェクト情報
   */
  projectInfo: IProjectRow;
  /**
   * ファイル情報一覧
   */
  rows: IFileRow[];
  /**
   * 戻るボタンが押された場合に呼び出されます
   */
  onBack: () => void;
  /**
   * ファイル一覧をロードする場合に呼び出します
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoadFiles: (loading: boolean) => void;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * メッセージBoxを表示します
   * @param params - メッセージBoxパラメータ
   */
  showMessage: (params: IMessageBoxParams) => void;
  /**
   * 確認ダイアログを表示します。
   * @param params - 確認ダイアログパラメータ
   */
  showConfirm: (param: IConfirmDialogParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
  /**
   * 非操作タイムアウトをリセットする場合に呼び出します
   */
  resetUnoperatedTimeout: () => void;
}

interface IState {
  /**
   * 拡大ダイアログを表示する場合はtrue
   */
  showExpansionDialog: boolean;
  /**
   * 選択されたチャート描画情報
   */
  selectProt: IProtData | undefined;
  /**
   * ダイアログチャート幅
   */
  width: number;
  /**
   * ダイアログチャート高さ
   */
  height: number;
  /**
   * シミュレーション結果一覧
   */
  simRows: ISimulationResultsRow[];
}

class ResultsSimulationPanel extends React.Component<IProps, IState> {
  private readonly dialog_area_correction =
    ChartDialogConfig.CHAERT_DIALOG_AREA_CORRECTION;

  constructor(props: IProps) {
    super(props);
    this.state = {
      showExpansionDialog: false,
      selectProt: undefined,
      width: window.innerWidth * this.dialog_area_correction,
      height: window.innerHeight * this.dialog_area_correction,
      simRows: []
    };

    this.handleCloseExpansionDialog = this.handleCloseExpansionDialog.bind(this);
    this.handleOpenExpansionDialog = this.handleOpenExpansionDialog.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.getSimulationResultsRow = this.getSimulationResultsRow.bind(this);
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
    this.getSimulationResultsRow();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  handleResize() {
    const { innerWidth, innerHeight } = window;
    this.setState({
      width: innerWidth * this.dialog_area_correction,
      height: innerHeight * this.dialog_area_correction,
    });
  }

  handleOpenExpansionDialog(rowid: number) {
    const { simRows } = this.state;
    const { onLoading } = this.props;
    onLoading(true); 
    const row = simRows.find((row) => {
      return row.id === rowid;
    });
    if (row) {
      const id = row.id;
      const simProt: IProtData = {
        fileid: rowid.toString(),
        fname: simRows[id].type,
        datanum: simRows[id].simdatanum,
        interval: simRows[id].interval,
        input: simRows[id].inrows,
        output: simRows[id].outrows,
      };
      this.setState({
        showExpansionDialog: true,
        selectProt: simProt,
      });     
    }
    setTimeout(() => {
      onLoading(false); 
    });
  }

  handleCloseExpansionDialog() {
    this.setState({
      showExpansionDialog: false,
      selectProt: undefined,
    });
  }

  getSimulationResultsRow() {
    const {
      authService,
      projectInfo,
      onCommunicationError,
      onLoading,
      resetUnoperatedTimeout,
    } = this.props
    resetUnoperatedTimeout();
    onLoading(true);
    getSimulationResults({
      authService: authService,
      projectid: projectInfo.projectid,
      success: (res) => {               
        this.setState({
          simRows: res,
        });
        onLoading(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
        onLoading(false);      
      },
    });  
  }

  getContents() {
    let contents = <div />
    contents = (
      <ResultsSimulation
        mainSpaceWidth={this.props.mainSpaceWidth} 
        rows={this.props.rows} 
        simRows={this.state.simRows} 
        onSelecRow={this.handleOpenExpansionDialog}
        onLoading={this.props.onLoading}
        onLoadFiles={this.getSimulationResultsRow}
      />
    );  
    return contents;
  }

  render() {
    const { projectInfo, onBack, t } = this.props
    const {
      showExpansionDialog,
      selectProt,
      width,
      height,
    } = this.state
    const contents = this.getContents();
    
    const dialog: CSSProperties | undefined = showExpansionDialog
      ? undefined
      : { display: "none" }; 
    return (
      <div className="mainspace">
        <div className="mainspace-title">
          <h3>{t("project_detail_project_name")}</h3>
          <h3 style={{ marginLeft: "10px" }}>{projectInfo.name}</h3>
        </div>
        <div className="mainspace-toolbar">
          <BackButton onClick={onBack} />
        </div>
        <div className="mainspace-toolbar">
          <h3>{t("simulator_simulation_results")}</h3>
        </div>
        <div>{contents}</div>
        <div className="overlay" style={dialog}>
          <TrimmingChartDialog
            show={showExpansionDialog}
            width={width}
            height={height}
            edit={false}
            cheight={height * 0.5}
            prot={selectProt}
            onCancel={this.handleCloseExpansionDialog}
          />
        </div>
      </div>
    );
  }
}

/**
 * シミュレータのシミュレーション結果画面
 */
export default withTranslation()(ResultsSimulationPanel);